import { SettingOutlined } from '@ant-design/icons'
import { Button, Divider, Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useTreatmentServiceGetEpidemiologyCaseListEndpoint } from 'src/api/queries'
import {
  EpidemiologyCasePagedListItem,
  EpidemiologyCaseStatus,
  Gender
} from 'src/api/requests'
import {
  GenderSelect,
  PageBreadcrumbs,
  RangePicker,
  SearchBar
} from 'src/components'
import { useTablePagination, useUser } from 'src/hooks'
import {
  dayMonthYearFormatWithDot,
  dayMonthYearHourMinutesFormatWithDot
} from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { resetPagination } from 'src/utils'

import { NewCasesCard } from '../components'

export const CasesPage = () => {
  const { t, currentLang } = useLocales()
  const navigate = useNavigate()

  const pagination = useTablePagination()
  const { userData } = useUser()

  const [isOpenFilters, setIsOpenFilters] = useState(false)
  const [search, setSearch] = useState<string>()
  const [gender, setGender] = useState<string>()
  const [createdAtFrom, setCreatedAtFrom] = useState<string>()
  const [createdAtTo, setCreatedAtTo] = useState<string>()
  const [patientBirthDateFrom, setPatientBirthDateFrom] = useState<string>()
  const [patientBirthDateTo, setPatientBirthDateTo] = useState<string>()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    institutionId: userData?.currentLegalEntityId,
    status: EpidemiologyCaseStatus.SYNCHRONISED,
    ...(!!search && {
      searchTerm: search
    }),
    ...(gender && {
      patientGender: gender as Gender
    }),

    ...(createdAtFrom &&
      createdAtTo && {
        createdAtFrom,
        createdAtTo
      }),
    ...(patientBirthDateFrom &&
      patientBirthDateTo && {
        patientBirthDateFrom,
        patientBirthDateTo
      })
  }

  const { data, isLoading } =
    useTreatmentServiceGetEpidemiologyCaseListEndpoint(queryParams)

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const onGenderSelect = (val: string) => {
    setGender(val)
    resetPagination(pagination)
  }

  const onGenderClear = () => {
    setGender(undefined)
    resetPagination(pagination)
  }

  const onToggleFilters = () => setIsOpenFilters((prev) => !prev)

  const columns: ColumnsType<EpidemiologyCasePagedListItem> = [
    {
      key: 'epidemiology number',
      title: t('epidemiology.epidemiology_number'),
      fixed: 'left',
      render: (_, { code }) => code || '-'
    },
    {
      key: 'synchronised at',
      title: t('epidemiology.synchronised_at'),
      render: (_, { synchronisedAt }) =>
        dayjs
          .utc(synchronisedAt)
          .tz()
          .format(dayMonthYearHourMinutesFormatWithDot)
    },
    {
      key: 'created at',
      title: t('epidemiology.created_at'),
      render: (_, { createdAt }) =>
        dayjs.utc(createdAt).tz().format(dayMonthYearHourMinutesFormatWithDot)
    },

    {
      key: 'sime code',
      title: t('common.sime_code'),
      render: (_, { patient }) => patient?.simeCode
    },
    {
      key: 'tarv code',
      title: t('common.tarv_code'),
      render: (_, { patient }) => patient?.tarvCode
    },
    {
      key: 'birth date',
      title: t('form.birth_date'),
      render: (_, { patient }) =>
        dayjs(patient?.birthDate).format(dayMonthYearFormatWithDot)
    },
    {
      key: 'gender',
      title: t('form.gender'),
      render: (_, { patient }) => (patient?.gender === Gender.MALE ? 'M' : 'F')
    },
    {
      key: 'cuatm code',
      title: t('organizational_structure.cuatm_code'),
      render: (_, { patient }) => patient?.cuatmLocality?.code
    },
    {
      key: 'locality',
      title: t('organizational_structure.city'),
      render: (_, { patient }) =>
        patient?.cuatmLocality?.translation?.[currentLang.value]
    },
    {
      key: 'institution',
      title: t('cases.institution'),
      render: (_, { institution }) => institution?.name
    },
    {
      key: 'actions',
      fixed: 'right',
      render: ({ id }) => (
        <Button
          type="link"
          onClick={() => navigate(paths.epidemiologyCases.view(id))}
        >
          {t('common.details')}
        </Button>
      )
    }
  ]

  return (
    <PageBreadcrumbs
      title={t('menu.cases')}
      items={[{ title: t('menu.cases') }]}
    >
      <Flex vertical>
        <Flex vertical gap={16}>
          <Flex justify="space-between">
            <SearchBar
              onSearch={onSearch}
              placeholder={t('investigations.search_by_person_code')}
            />

            <Button icon={<SettingOutlined />} onClick={onToggleFilters}>
              {t('common.filters')}
            </Button>
          </Flex>

          <Flex
            wrap="wrap"
            gap={8}
            style={{ display: isOpenFilters ? 'flex' : 'none' }}
          >
            <GenderSelect
              style={{ minWidth: 200 }}
              onSelect={onGenderSelect}
              allowClear
              onClear={onGenderClear}
            />

            <RangePicker
              style={{ minWidth: 400 }}
              setDateFrom={setCreatedAtFrom}
              setDateTo={setCreatedAtTo}
              resetPagination={() => resetPagination(pagination)}
              placeholder={[
                t('tests.created_from_date'),
                t('tests.created_to_date')
              ]}
            />

            <RangePicker
              style={{ minWidth: 400 }}
              setDateFrom={setPatientBirthDateFrom}
              setDateTo={setPatientBirthDateTo}
              resetPagination={() => resetPagination(pagination)}
              placeholder={[
                t('persons.from_date_of_birth'),
                t('persons.to_date_of_birth')
              ]}
            />
          </Flex>
        </Flex>

        <Divider />

        <NewCasesCard />

        <Table
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={data?.data}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
